import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Typography } from '@material-ui/core';
import { MODES } from '../../../../utils/constant';
import { convertUnit, getAirUnit, getMarks, roundSetPointRange } from '../../../../utils/utility';
import { RangeSlider } from '../../../../components/RangeSlider';
import { useMobileStyles } from '../styles';
import I18n from '../../../../localization';

const TemperatureControl = ({
  iqAirDetails,
  IQAirPoints,
  IQAirRange,
  IQAirTemperatureRange,
  availableModes,
  forceDualMode = false,
  isDarkMode,
  onSliderChange,
  onSliderAfterChange
}) => {
  const classes = useMobileStyles();
  const sliderWrapperRef = useRef(null);
  const isF = getAirUnit() === "F";

  const setDraggingClass = (isDragging) => {
    const wrapperEl = sliderWrapperRef.current;
    if (!wrapperEl) return;

    if (isDragging) {
      wrapperEl.classList.add("rs__is-dragging");
    } else {
      wrapperEl.classList.remove("rs__is-dragging");
    }
  };

  const setDraggingThumbClass = (thumbIndex) => {
    const wrapperEl = sliderWrapperRef.current;
    if (!wrapperEl) return;

    const thumbs = wrapperEl.querySelectorAll('.rs__thumb');
    thumbs.forEach((thumb) => {
      thumb.classList.remove('rs__thumb-dragging');
    });

    if (!Number.isInteger(thumbIndex) || thumbIndex < 0 || thumbIndex >= thumbs.length) {
      return;
    }

    thumbs[thumbIndex].classList.add('rs__thumb-dragging');
  };

  const resolvedRanges = useMemo(() => {
    const getConvertedBound = (value, boundType) => {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return null;

      const convertedValue = roundSetPointRange(convertUnit(numericValue));
      if (!isF && boundType === 'min') {
        return Math.ceil(convertedValue);
      }

      return convertedValue;
    };

    const heatRange = {
      min: getConvertedBound(IQAirTemperatureRange?.['heat-setpoint-range']?.min, 'min'),
      max: getConvertedBound(IQAirTemperatureRange?.['heat-setpoint-range']?.max, 'max')
    };

    const coolRange = {
      min: getConvertedBound(IQAirTemperatureRange?.['cool-setpoint-range']?.min, 'min'),
      max: getConvertedBound(IQAirTemperatureRange?.['cool-setpoint-range']?.max, 'max')
    };

    return {
      heatRange,
      coolRange,
      unionRange: {
        min: IQAirRange?.min,
        max: IQAirRange?.max
      }
    };
  }, [IQAirTemperatureRange, IQAirRange]);

  const sliderConfig = useMemo(() => {
    const step = isF ? 1 : 0.5;
    const minDistance = isF ? 3 : 1.5;

    const supportsHeat = Array.isArray(availableModes) && availableModes.includes(MODES.HEAT);
    const supportsCool = Array.isArray(availableModes) && availableModes.includes(MODES.COOL);
    const isDualMode = supportsHeat && supportsCool;
    const isAutoMode = isDualMode && (forceDualMode || iqAirDetails.airMode === MODES.AUTO);

    const fallbackToHeat = supportsHeat && !supportsCool;
    const fallbackToCool = supportsCool && !supportsHeat;

    const isHeatMode = !isAutoMode && (iqAirDetails.airMode === MODES.HEAT || fallbackToHeat);
    const isCoolMode = !isAutoMode && (iqAirDetails.airMode === MODES.COOL || fallbackToCool);

    const selectedRange = isHeatMode
      ? resolvedRanges.heatRange
      : isCoolMode
      ? resolvedRanges.coolRange
      : resolvedRanges.unionRange;

    const selectedMin = selectedRange && selectedRange.min;
    const selectedMax = selectedRange && selectedRange.max;

    const min = Number.isFinite(selectedMin)
      ? selectedMin
      : resolvedRanges.unionRange?.min;
    const max = Number.isFinite(selectedMax)
      ? selectedMax
      : resolvedRanges.unionRange?.max;

    return {
      min,
      max,
      step,
      minDistance,
      isDualMode,
      isHeatMode,
      isCoolMode,
      isAutoMode,
      value: isAutoMode
        ? [IQAirPoints.heat_setpoint, IQAirPoints.cool_setpoint]
        : isHeatMode
        ? [IQAirPoints.heat_setpoint]
        : [IQAirPoints.cool_setpoint],
      displayValue: isAutoMode
        ? `${IQAirPoints.heat_setpoint} - ${IQAirPoints.cool_setpoint}`
        : isHeatMode
        ? `${IQAirPoints.heat_setpoint}`
        : `${IQAirPoints.cool_setpoint}`
    };
  }, [IQAirPoints, iqAirDetails.airMode, isF, resolvedRanges, forceDualMode, availableModes]);

  useEffect(() => {
    if (sliderConfig.isAutoMode) return;
    if (!Number.isFinite(sliderConfig.min) || !Number.isFinite(sliderConfig.max)) return;

    const isHeatMode = sliderConfig.isHeatMode;
    const setpointType = isHeatMode ? 'heat_setpoint' : 'cool_setpoint';
    const currentValue = IQAirPoints[setpointType];

    if (!Number.isFinite(currentValue)) return;

    const clampedValue = Math.min(sliderConfig.max, Math.max(sliderConfig.min, currentValue));
    if (clampedValue === currentValue) return;

    onSliderChange([clampedValue], sliderConfig);
  }, [IQAirPoints, onSliderChange, sliderConfig]);

  const handleSliderChange = (val, thumbIndex) => {
    document.body.classList.add("slider-dragging");
    setDraggingClass(true);
    setDraggingThumbClass(thumbIndex);

    if (sliderConfig.isDualMode && sliderConfig.isAutoMode && Array.isArray(val)) {
      const getFallbackBound = (primary, fallback) => (
        Number.isFinite(primary) ? primary : fallback
      );

      const heatMin = getFallbackBound(resolvedRanges.heatRange?.min, sliderConfig.min);
      const heatMax = getFallbackBound(resolvedRanges.heatRange?.max, sliderConfig.max);
      const coolMin = getFallbackBound(resolvedRanges.coolRange?.min, sliderConfig.min);
      const coolMax = getFallbackBound(resolvedRanges.coolRange?.max, sliderConfig.max);

      const [heat, cool] = val;
      const nextHeat = Math.min(heatMax, Math.max(heatMin, heat));
      const nextCool = Math.min(coolMax, Math.max(coolMin, cool));

      onSliderChange([nextHeat, nextCool], sliderConfig);
      return;
    }

    onSliderChange(val, sliderConfig);
  };

  const handleSliderAfterChange = () => {
    document.body.classList.remove("slider-dragging");
    setDraggingClass(false);
    setDraggingThumbClass(-1);
    onSliderAfterChange();
  };

  useEffect(() => {
    return () => {
      document.body.classList.remove("slider-dragging");
      setDraggingClass(false);
      setDraggingThumbClass(-1);
    };
  }, []);

  if (!sliderConfig.isAutoMode && !sliderConfig.isHeatMode && !sliderConfig.isCoolMode) {
    return null;
  }

  return (
    <div className={''}>
      <Box className={[classes.IQCard, classes.IQAirMode, classes.InnerCards].join(" ")}>
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems:'baseline' }}>
          <Typography type='label' className={classes.cardInnerLabel}>
            {I18n.t('mobile.iqAirSetting.setPointsText')}
          </Typography>
          <Box className={classes.setpointValueContainer}>
            {sliderConfig.isAutoMode ? (
              <>
                <Typography
                  type='label'
                  className={`${classes.setpointValueText} ${isDarkMode ? classes.heatSetpointDark : classes.heatSetpointLight}`}
                >
                  {IQAirPoints.heat_setpoint}°{getAirUnit()}
                </Typography>
                <Typography type='label' className={classes.setpointSeparator}>
                  -
                </Typography>
                <Typography
                  type='label'
                  className={`${classes.setpointValueText} ${isDarkMode ? classes.coolSetpointDark : classes.coolSetpointLight}`}
                >
                  {IQAirPoints.cool_setpoint}°{getAirUnit()}
                </Typography>
              </>
            ) : sliderConfig.isHeatMode ? (
              <Typography
                type='label'
                className={`${classes.setpointValueText} ${isDarkMode ? classes.heatSetpointDark : classes.heatSetpointLight}`}
              >
                {IQAirPoints.heat_setpoint}°{getAirUnit()}
              </Typography>
            ) : (
              <Typography
                type='label'
                className={`${classes.setpointValueText} ${isDarkMode ? classes.coolSetpointDark : classes.coolSetpointLight}`}
              >
                {IQAirPoints.cool_setpoint}°{getAirUnit()}
              </Typography>
            )}
          </Box>
        </Box>

        <Box className={classes.modeCard} sx={{ height: '100px' }}>
          <RangeSlider
            key={iqAirDetails.airMode}
            sliderRef={sliderWrapperRef}
            className={`${classes.dualTempSlider} ${
              sliderConfig.isHeatMode
                ? 'heat'
                : sliderConfig.isCoolMode
                ? 'cool'
                : ''
            }`}
            min={sliderConfig.min}
            max={sliderConfig.max}
            step={sliderConfig.step}
            value={sliderConfig.value}
            marks={getMarks(sliderConfig.min, sliderConfig.max, isF)}
            minDistance={sliderConfig.isAutoMode ? sliderConfig.minDistance : 0}
            onChange={handleSliderChange}
            onAfterChange={handleSliderAfterChange}
          />
        </Box>
      </Box>
    </div>
  );
};

export default TemperatureControl;
